import type { AnonymousMe, ConsentRequest, ConsentResponse } from "@/types";
import { apiDelete, apiGet, apiPost } from "./client";

/**
 * Endpoints liés à la session anonyme (UUID + cookie httpOnly `sunmatch_anon`).
 * Voir ADR-002 et `docs/api/endpoints.md` § 2.
 *
 * Le préfixe canonique côté backend est `/api/v1/sessions/*` (cf. `app/sessions/router.py`).
 */

const BASE = "/api/v1/sessions";

/**
 * Lit l'identité courante (cookie déjà posé). Renvoie ``null`` si le
 * cookie est absent ou pointe vers une session inconnue (H7-6 — contrat
 * « lenient » côté backend pour éviter le log XHR 401 bruyant au boot).
 *
 * Toutes les autres routes API (sites, simulations, …) gardent leur 401
 * ``ANONYMOUS_SESSION_REQUIRED`` ; seul `/sessions/me` est lenient.
 */
export function getMe(): Promise<AnonymousMe | null> {
  return apiGet<AnonymousMe | null>(`${BASE}/me`);
}

/**
 * Crée une session anonyme et pose le cookie `sunmatch_anon`.
 * À appeler en bootstrap si `getMe` a renvoyé 401.
 */
export function createAnonymousSession(): Promise<AnonymousMe> {
  return apiPost<AnonymousMe>(`${BASE}/anonymous`);
}

/**
 * Supprime définitivement la session anonyme courante et toutes les données
 * rattachées (sites, simulations, imports, séries temporelles). Le backend
 * invalide aussi le cookie `sunmatch_anon` (Set-Cookie Max-Age=0).
 *
 * RGPD art. 17 — droit à l'effacement. Action irréversible.
 * Cf. S3-12a backend + `docs/api/endpoints.md`.
 */
export function deleteCurrentSession(): Promise<void> {
  return apiDelete<void>(`${BASE}/me`);
}

/**
 * Consentement applicatif RGPD SunMatch (S6-4 / S6-5, GAP-2, R-13).
 *
 * Endpoint **idempotent** côté backend : renvoie toujours 200 (jamais 201).
 * Erreurs possibles :
 * - 401 `ANONYMOUS_SESSION_REQUIRED` si cookie absent (le client `apiClient`
 *   bootstrappe alors une session anonyme et rejoue l'appel automatiquement).
 * - 422 si `consent_version` ne matche pas la regex `^v\d+\.\d+$`.
 *
 * Le cookie `sunmatch_anon` est transmis via `withCredentials: true`.
 */
export function postSessionConsent(
  version: string,
): Promise<ConsentResponse> {
  const body: ConsentRequest = { consent_version: version };
  return apiPost<ConsentResponse, ConsentRequest>(`${BASE}/consent`, body);
}
